import { useEffect, useState } from 'react';
import { Edit, Plus, Power } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { DocumentTypeItem, DocumentTypePayload } from '../api/documentos';
import { CONFIDENTIALITY_LABELS, DOCUMENT_CATEGORY_LABELS } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useSalvarTipoDocumental, useSituacaoTipoDocumental, useTiposDocumentais } from '../hooks/cadastro';

const KINDS = ['pdf', 'png', 'jpeg'];
const TYPES = ['clt', 'pj'];

/** Parametrização dos tipos documentais (RN-GP-002-006). */
export default function TiposDocumentaisPage() {
  const toast = useToast();
  const [active, setActive] = useState<'true' | 'false' | 'all'>('true');
  const tipos = useTiposDocumentais(active);
  const situacao = useSituacaoTipoDocumental();
  const [editing, setEditing] = useState<DocumentTypeItem | 'novo' | null>(null);

  async function alternar(t: DocumentTypeItem) {
    try {
      await situacao.mutateAsync({ id: t.id, active: !t.active });
      toast.success(t.active ? 'Tipo inativado.' : 'Tipo reativado.');
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível alterar a situação.'));
    }
  }

  return (
    <PageContainer
      title="Tipos documentais"
      subtitle="Categoria, confidencialidade, validade, formatos e obrigatoriedade de cada documento."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Tipos documentais' }]}
      action={
        <Can feature={FEATURES.pessoasTiposDocumentais} action="create">
          <Button onClick={() => setEditing('novo')}><Plus className="mr-2 h-5 w-5" /> Novo tipo</Button>
        </Can>
      }
    >
      <div className="min-w-[200px] max-w-xs">
        <AutocompleteSelect
          label="Situação"
          options={[{ value: 'true', label: 'Ativos' }, { value: 'false', label: 'Inativos' }, { value: 'all', label: 'Todos' }]}
          value={active}
          onChange={(v) => setActive(v as typeof active)}
        />
      </div>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3">Confidencialidade</th>
              <th className="px-4 py-3">Obrigatório para</th>
              <th className="px-4 py-3">Validade</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(tipos.data ?? []).map((t) => (
              <tr key={t.id} className={t.active ? '' : 'opacity-60'}>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{t.name}</p>
                  <p className="font-mono text-xs text-gray-500">{t.code} · {t.allowedKinds.join(', ').toUpperCase()} · {t.maxSizeMb} MB</p>
                </td>
                <td className="px-4 py-3">{DOCUMENT_CATEGORY_LABELS[t.category]}</td>
                <td className="px-4 py-3">{CONFIDENTIALITY_LABELS[t.defaultConfidentiality]}</td>
                <td className="px-4 py-3">
                  {t.requiredFor.length === 0 ? '—' : t.requiredFor.map((x) => x.toUpperCase()).join(', ')}
                  {t.blocksAdmissionFor.length > 0 && (
                    <p className="text-xs text-red-600">Bloqueia admissão: {t.blocksAdmissionFor.map((x) => x.toUpperCase()).join(', ')}</p>
                  )}
                </td>
                <td className="px-4 py-3">{t.hasExpiry ? `${t.defaultValidityMonths ?? '—'} meses · alerta ${t.expiryAlertDays} dias` : 'Indeterminada'}</td>
                <td className="px-4 py-3 text-right">
                  <Can feature={FEATURES.pessoasTiposDocumentais} action="edit">
                    <div className="flex justify-end gap-1">
                      <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setEditing(t)} aria-label={`Editar ${t.name}`}>
                        <Edit className="h-4 w-4" />
                      </button>
                      <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => alternar(t)}
                        aria-label={t.active ? `Inativar ${t.name}` : `Reativar ${t.name}`}>
                        <Power className="h-4 w-4" />
                      </button>
                    </div>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TipoModal value={editing} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); toast.success(m); }} />
    </PageContainer>
  );
}

const EMPTY: DocumentTypePayload & { code: string } = {
  code: '', name: '', category: 'pessoal', defaultConfidentiality: 'interno', hasExpiry: false, defaultValidityMonths: null,
  expiryAlertDays: 30, allowedKinds: ['pdf', 'png', 'jpeg'], maxSizeMb: 10, requiredFor: [], blocksAdmissionFor: [],
  visibleToCollaborator: true, collaboratorCanUpload: true, segregatedValidation: false,
};

function TipoModal({ value, onClose, onSaved }: { value: DocumentTypeItem | 'novo' | null; onClose: () => void; onSaved: (m: string) => void }) {
  const salvar = useSalvarTipoDocumental();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const editing = value && value !== 'novo' ? value : null;

  useEffect(() => {
    if (!value) return;
    setError(null);
    setForm(editing ? { ...EMPTY, ...editing } : EMPTY);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (list: string[], item: string) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  async function submit() {
    if (!editing && !/^[a-z0-9_]{2,40}$/.test(form.code)) return setError('Código: 2 a 40 letras minúsculas, números ou "_".');
    if (!form.name.trim()) return setError('Informe o nome.');
    if (form.allowedKinds.length === 0) return setError('Selecione ao menos um formato.');
    const { code, ...rest } = form;
    const payload = {
      ...rest,
      blocksAdmissionFor: form.blocksAdmissionFor.filter((t) => form.requiredFor.includes(t)),
      defaultValidityMonths: form.hasExpiry ? form.defaultValidityMonths : null,
    };
    try {
      await salvar.mutateAsync({ id: editing?.id ?? null, payload: editing ? payload : { ...payload, code } as never });
      onSaved(editing ? 'Tipo atualizado.' : 'Tipo criado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar o tipo.'));
    }
  }

  const check = (label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}</label>
  );

  return (
    <PessoasModal open={value !== null} size="lg" title={editing ? `Editar — ${editing.name}` : 'Novo tipo documental'}
      submitLabel="Salvar" isSubmitting={salvar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput label="Código" value={form.code} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))} />
        <TextInput label="Nome" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        <AutocompleteSelect label="Categoria" options={Object.entries(DOCUMENT_CATEGORY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} />
        <AutocompleteSelect label="Confidencialidade" options={Object.entries(CONFIDENTIALITY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          value={form.defaultConfidentiality} onChange={(v) => setForm((f) => ({ ...f, defaultConfidentiality: v as DocumentTypePayload['defaultConfidentiality'] }))} />
        <TextInput label="Tamanho máximo (MB)" type="number" value={String(form.maxSizeMb)}
          onChange={(e) => setForm((f) => ({ ...f, maxSizeMb: Number(e.target.value) || 1 }))} />
        <TextInput label="Antecedência do alerta de vencimento (dias)" type="number" value={String(form.expiryAlertDays)}
          onChange={(e) => setForm((f) => ({ ...f, expiryAlertDays: Number(e.target.value) || 0 }))} />
      </div>
      <div className="flex flex-wrap gap-4">
        <span className="text-sm font-medium text-gray-700">Formatos:</span>
        {KINDS.map((k) => check(k.toUpperCase(), form.allowedKinds.includes(k), () => setForm((f) => ({ ...f, allowedKinds: toggle(f.allowedKinds, k) }))))}
      </div>
      <div className="flex flex-wrap gap-4">
        <span className="text-sm font-medium text-gray-700">Obrigatório para:</span>
        {TYPES.map((t) => check(t.toUpperCase(), form.requiredFor.includes(t), () => setForm((f) => ({ ...f, requiredFor: toggle(f.requiredFor, t) }))))}
        <span className="text-sm font-medium text-gray-700">Bloqueia a admissão:</span>
        {TYPES.filter((t) => form.requiredFor.includes(t)).map((t) =>
          check(t.toUpperCase(), form.blocksAdmissionFor.includes(t), () => setForm((f) => ({ ...f, blocksAdmissionFor: toggle(f.blocksAdmissionFor, t) }))))}
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {check('Tem validade', form.hasExpiry, (v) => setForm((f) => ({ ...f, hasExpiry: v })))}
        {form.hasExpiry && (
          <TextInput label="Validade padrão (meses)" type="number" value={String(form.defaultValidityMonths ?? '')}
            onChange={(e) => setForm((f) => ({ ...f, defaultValidityMonths: Number(e.target.value) || null }))} />
        )}
        {check('Visível ao colaborador', form.visibleToCollaborator, (v) => setForm((f) => ({ ...f, visibleToCollaborator: v })))}
        {check('Colaborador pode enviar', form.collaboratorCanUpload, (v) => setForm((f) => ({ ...f, collaboratorCanUpload: v })))}
        {check('Quem inclui não valida (segregação)', form.segregatedValidation, (v) => setForm((f) => ({ ...f, segregatedValidation: v })))}
      </div>
    </PessoasModal>
  );
}
